import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/lib/i18n';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock, ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';
import { useState, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import placeholderCaseCover from '@/assets/placeholder-case-cover.png';

const CaseCarousel = ({ images }: { images: string[] }) => {
  const { t } = useI18n();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  if (images.length === 0) {
    return (
      <div className="aspect-video rounded-2xl overflow-hidden bg-secondary flex flex-col items-center justify-center gap-4">
        <div className="w-16 h-16 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center">
          <ImageIcon className="text-muted-foreground/50" size={28} />
        </div>
        <p className="text-muted-foreground/40 text-sm font-display">{t('Изображения скоро появятся', 'Images coming soon')}</p>
      </div>
    );
  }

  return (
    <div className="relative group">
      <div className="overflow-hidden rounded-2xl" ref={emblaRef}>
        <div className="flex">
          {images.map((src, i) => (
            <div key={i} className="flex-[0_0_100%] min-w-0">
              <div className="aspect-video bg-secondary">
                <img src={src} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            </div>
          ))}
        </div>
      </div>
      {images.length > 1 && (
        <>
          <button onClick={scrollPrev} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/90">
            <ChevronLeft size={20} />
          </button>
          <button onClick={scrollNext} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/90">
            <ChevronRight size={20} />
          </button>
        </>
      )}
    </div>
  );
};

const CasePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { lang, t } = useI18n();
  const [passwordInput, setPasswordInput] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const { data: caseItem, isLoading } = useQuery({
    queryKey: ['case', slug],
    queryFn: async () => {
      const { data } = await supabase
        .from('cases')
        .select('*')
        .eq('slug', slug!)
        .maybeSingle();
      return data;
    },
    enabled: !!slug,
  });

  const { data: sections } = useQuery({
    queryKey: ['case-sections', caseItem?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('case_sections')
        .select('*')
        .eq('case_id', caseItem!.id)
        .order('sort_order');
      return data || [];
    },
    enabled: !!caseItem?.id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isPlaceholder = slug?.startsWith('placeholder');

  if (!caseItem && !isPlaceholder) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-display font-bold mb-4">{t('Проект не найден', 'Project not found')}</h1>
          <Link to="/" className="text-primary hover:underline">{t('На главную', 'Go home')}</Link>
        </div>
      </div>
    );
  }

  if (isPlaceholder && !caseItem) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <section className="pt-28 md:pt-36 pb-12">
          <div className="container">
            <Link to="/portfolio" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8">
              <ArrowLeft size={16} />
              {t('Назад', 'Back')}
            </Link>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <p className="text-primary font-display text-sm font-medium tracking-[0.3em] uppercase mb-4">
                {t('Веб-дизайн', 'Web Design')}
              </p>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold leading-tight mb-6">
                {t('Название проекта', 'Project Title')}
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl">
                {t(
                  'Краткое описание проекта, его цели и задачи. Здесь можно рассказать о том, что было сделано и какой результат был достигнут.',
                  'A brief description of the project, its goals and objectives. Here you can describe what was done and what results were achieved.'
                )}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Cover image placeholder */}
        <section className="pb-16">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="aspect-video rounded-2xl overflow-hidden bg-secondary"
            >
              <img src={placeholderCaseCover} alt={t('Фото проекта', 'Project photo')} className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
            </motion.div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="container">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-display font-bold mb-4">
                {t('О проекте', 'About the project')}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed whitespace-pre-line">
                {t(
                  'Этот проект был создан для демонстрации возможностей нашей студии. Мы разработали уникальный дизайн, который отражает ценности и миссию клиента.\n\nВ процессе работы мы провели глубокий анализ целевой аудитории, разработали прототипы и провели серию тестирований для достижения оптимального результата.\n\nРезультатом стал современный, функциональный и эстетически привлекательный продукт, который помогает клиенту достигать своих бизнес-целей.',
                  'This project was created to demonstrate the capabilities of our studio. We developed a unique design that reflects the client\'s values and mission.\n\nDuring the process, we conducted deep audience analysis, developed prototypes, and ran a series of tests to achieve optimal results.\n\nThe result is a modern, functional, and aesthetically appealing product that helps the client achieve their business goals.'
                )}
              </p>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="container">
            <blockquote className="max-w-3xl mx-auto border-l-4 border-primary pl-6 py-2">
              <p className="text-xl md:text-2xl font-display italic text-foreground">
                {t(
                  '«Работа с этой командой была невероятным опытом. Они превзошли все наши ожидания.»',
                  '"Working with this team was an incredible experience. They exceeded all our expectations."'
                )}
              </p>
              <cite className="block mt-4 text-muted-foreground not-italic">
                — {t('Клиент', 'Client')}
              </cite>
            </blockquote>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  const title = lang === 'ru' ? caseItem.title_ru : caseItem.title_en;
  const description = lang === 'ru' ? caseItem.description_ru : caseItem.description_en;
  const category = lang === 'ru' ? caseItem.category_ru : caseItem.category_en;
  const heroVideoSection = sections?.find((s) => s.section_type === 'hero_video');
  const heroCarouselSection = sections?.find((s) => s.section_type === 'hero_carousel');
  const heroImageSection = sections?.find((s) => s.section_type === 'hero_image');
  const heroFallbackImage = heroVideoSection?.image_url || null;

  // Password gate
  const needsPassword = caseItem.password && !isUnlocked;

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === caseItem.password) {
      setIsUnlocked(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  if (needsPassword) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[70vh]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-md w-full px-6"
          >
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-6">
              <Lock className="text-primary" size={28} />
            </div>
            <h1 className="text-2xl md:text-3xl font-display font-bold mb-2">{title}</h1>
            <p className="text-muted-foreground mb-8">
              {t('Этот кейс защищён паролем', 'This case is password protected')}
            </p>
            <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
                placeholder={t('Введите пароль', 'Enter password')}
                className="w-full px-4 py-3 rounded-xl bg-secondary border border-border text-foreground text-center text-lg tracking-widest placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
              {passwordError && (
                <p className="text-destructive text-sm">{t('Неверный пароль', 'Incorrect password')}</p>
              )}
              <button
                type="submit"
                className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-display font-medium hover:opacity-90 transition-opacity"
              >
                {t('Открыть', 'Unlock')}
              </button>
            </form>
            <Link to="/portfolio" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mt-6 text-sm">
              <ArrowLeft size={14} />
              {t('Назад', 'Back')}
            </Link>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12">
        <div className="container">
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8">
            <ArrowLeft size={16} />
            {t('Назад', 'Back')}
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {category && (
              <p className="text-primary font-display text-sm font-medium tracking-[0.3em] uppercase mb-4">
                {category}
              </p>
            )}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold leading-tight mb-6">
              {title}
            </h1>
            {description && (
              <p className="text-xl text-muted-foreground max-w-2xl">{description}</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Cover media — carousel for case-11, video if available, otherwise case-specific image or placeholder */}
      <section className="pb-16">
        <div className="container">
          {heroCarouselSection ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <CaseCarousel images={heroCarouselSection.image_url ? heroCarouselSection.image_url.split(',').map(s => s.trim()).filter(Boolean) : []} />
            </motion.div>
          ) : heroImageSection?.image_url ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="aspect-video rounded-2xl overflow-hidden bg-secondary"
            >
              <img src={heroImageSection.image_url} alt={title} className="w-full h-full object-cover" />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="aspect-video rounded-2xl overflow-hidden bg-secondary"
            >
              {heroVideoSection?.video_url ? (
                heroVideoSection.video_url.match(/\.(mp4|webm|mov|ogg)(\?|$)/i) ? (
                  <video
                    src={heroVideoSection.video_url}
                    className="w-full h-full object-cover"
                    controls
                    playsInline
                    muted
                  />
                ) : (
                  <iframe
                    src={heroVideoSection.video_url}
                    className="w-full h-full"
                    allow="fullscreen"
                    allowFullScreen
                  />
                )
              ) : heroFallbackImage ? (
                <img src={heroFallbackImage} alt={title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <div className="w-16 h-16 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground/50 ml-1">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  </div>
                  <p className="text-muted-foreground/40 text-sm font-display">{t('Видео скоро появится', 'Video coming soon')}</p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* Sections */}
      {sections && sections.filter(s => s.section_type !== 'hero_carousel' && s.section_type !== 'hero_video' && s.section_type !== 'hero_image').map((section, i) => {
        const sTitle = lang === 'ru' ? section.title_ru : section.title_en;
        const sText = lang === 'ru' ? section.text_ru : section.text_en;

        return (
          <motion.section
            key={section.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="py-12 md:py-16"
          >
            <div className="container">
              {section.section_type === 'text_image' && (
                <div className={`grid md:grid-cols-2 gap-8 md:gap-16 items-center ${section.layout === 'right' ? '' : 'md:[direction:rtl] md:[&>*]:direction-ltr'}`}>
                  <div className={section.layout === 'right' ? 'md:order-2' : ''}>
                    {sTitle && <h2 className="text-2xl md:text-4xl font-display font-bold mb-4">{sTitle}</h2>}
                    {sText && <p className="text-muted-foreground text-lg leading-relaxed">{sText}</p>}
                  </div>
                  {section.image_url && (
                    <div className="aspect-[4/3] rounded-xl overflow-hidden bg-secondary">
                      <img src={section.image_url} alt={sTitle || ''} className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              )}

              {(section.section_type === 'image_only' || section.section_type === 'full_image') && section.image_url && (
                <div className={`${section.layout === 'full' || section.section_type === 'full_image' ? '' : 'max-w-4xl mx-auto'} rounded-xl overflow-hidden bg-secondary`}>
                  <img src={section.image_url} alt={sTitle || ''} className="w-full object-cover" />
                </div>
              )}

              {section.section_type === 'image' && section.image_url && (() => {
                const images = section.image_url.split(',').map(s => s.trim()).filter(Boolean);
                return (
                  <div className="flex flex-col gap-8">
                    {images.map((src, idx) => (
                      <div key={idx} className={`${section.layout === 'full' ? 'aspect-video rounded-2xl' : 'max-w-4xl mx-auto rounded-xl'} w-full overflow-hidden bg-secondary`}>
                        <img src={src} alt={`${sTitle || 'Image'} ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                );
              })()}

              {section.section_type === 'carousel' && (() => {
                const images = section.image_url ? section.image_url.split(',').map(s => s.trim()).filter(Boolean) : [];
                return <CaseCarousel images={images} />;
              })()}

              {section.section_type === 'video' && (
                <div className="aspect-video rounded-2xl overflow-hidden bg-secondary">
                  {section.video_url ? (
                    section.video_url.match(/\.(mp4|webm|mov|ogg)(\?|$)/i) ? (
                      <video
                        src={section.video_url}
                        className="w-full h-full object-cover"
                        controls
                        playsInline
                        muted
                      />
                    ) : (
                      <iframe
                        src={section.video_url}
                        className="w-full h-full"
                        allow="fullscreen"
                        allowFullScreen
                      />
                    )
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                      <div className="w-16 h-16 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground/50 ml-1">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                      </div>
                      <p className="text-muted-foreground/40 text-sm font-display">{t('Видео скоро появится', 'Video coming soon')}</p>
                    </div>
                  )}
                </div>
              )}

              {(section.section_type === 'text_only' || section.section_type === 'text') && (
                <div>
                  {sTitle && <h2 className="text-2xl md:text-4xl font-display font-bold mb-4">{sTitle}</h2>}
                  {sText && <p className="text-muted-foreground text-lg leading-relaxed whitespace-pre-line">{sText}</p>}
                </div>
              )}

              {section.section_type === 'quote' && (
                <blockquote className="max-w-3xl mx-auto border-l-4 border-primary pl-6 py-2">
                  <p className="text-xl md:text-2xl font-display italic text-foreground">{sText}</p>
                  {sTitle && <cite className="block mt-4 text-muted-foreground not-italic">— {sTitle}</cite>}
                </blockquote>
              )}
            </div>
          </motion.section>
        );
      })}

      <Footer />
    </div>
  );
};

export default CasePage;
